import { FC, memo, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { MenuProps } from 'antd';
import { Dropdown } from 'antd';
import classnames from 'classnames';
import * as Flags from 'country-flag-icons/react/3x2';
import { langTexts } from 'languages/langTexts';

import { AppDispatch } from 'app/store';

import { selectAuthToken } from 'features/auth/authSelectors';
import authService from 'features/auth/authService';
import { selectAppLocale } from 'features/locale/localeSelectors';
import { setLocale } from 'features/locale/localeSlice';

import LOCALES from 'constants/locales';

import { ReactComponent as ArrowDownIcon } from 'assets/icons/arrowDown.svg';

import styles from './LanguageSelect.module.scss';

interface LanguageSelectProps {
  className?: string;
  onLanguageChange?: (locale: string) => void;
}

const FLAGS: Record<string, keyof typeof Flags> = {
  [LOCALES.ENGLISH_US]: 'US',
  [LOCALES.SPANISH_ES]: 'ES',
  // 'es-419' is a region; using MX as a common LatAm representative
  [LOCALES.SPANISH_LATAM]: 'MX',
  [LOCALES.PORTUGUESE_BR]: 'BR',
  [LOCALES.PORTUGUESE_PT]: 'PT',
  [LOCALES.FRENCH_FR]: 'FR',
  [LOCALES.RUSSIAN_RU]: 'RU',
  [LOCALES.GERMAN_DE]: 'DE',
  [LOCALES.HINDI_IN]: 'IN',
  [LOCALES.TURKISH_TR]: 'TR',
  [LOCALES.AZERBAIJANI_AZ]: 'AZ',
  [LOCALES.ROMANIAN_RO]: 'RO',
  [LOCALES.UKRAINIAN]: 'UA',
  [LOCALES.ITALIAN_IT]: 'IT'
};

const LanguageSelect: FC<LanguageSelectProps> = ({
  className = '',
  onLanguageChange
}) => {
  const dispatch: AppDispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const currentLocale = useSelector(selectAppLocale);
  const token = useSelector(selectAuthToken);

  const handleLanguageChange = useCallback(
    async (locale: string) => {
      dispatch(setLocale(locale));
      onLanguageChange?.(locale);

      if (token) {
        try {
          await authService.updatePlatformLanguage(locale);
        } catch (error) {
          console.error('Failed to update platform language:', error);
        }
      }
    },
    [dispatch, onLanguageChange, token]
  );

  const currentFlagCode = FLAGS[currentLocale] ?? FLAGS[LOCALES.ENGLISH_US];
  const CurrentFlag = currentFlagCode ? Flags[currentFlagCode] : null;
  const langShortText = langTexts.find(
    (lang) => lang.key === currentLocale
  )?.shortText;

  const menuItems = useMemo(() => {
    return langTexts
      .filter((lang) => lang.key !== currentLocale)
      .map((lang) => {
        const flagCode = FLAGS[lang.key];
        const Flag = flagCode ? Flags[flagCode] : null;
        return {
          key: lang.key,
          label: (
            <div className={styles.menuItem}>
              {Flag && (
                <div className={styles.flagIcon}>
                  <Flag className={styles.flag} />
                </div>
              )}
              <span>{lang.longText}</span>
            </div>
          )
        };
      });
  }, [currentLocale]);

  const menuProps: MenuProps = {
    items: menuItems,
    onClick: ({ key }) => handleLanguageChange(key)
  };

  return (
    <Dropdown
      menu={menuProps}
      placement="bottomRight"
      trigger={['click']}
      onOpenChange={setOpen}
      open={open}
    >
      <div className={classnames(styles.languageSelect, className)}>
        {CurrentFlag && <CurrentFlag className={styles.currentFlag} />}
        <span>{langShortText}</span>
        <ArrowDownIcon
          className={classnames(styles.arrow, { [styles.arrowOpen]: open })}
        />
      </div>
    </Dropdown>
  );
};

export default memo(LanguageSelect);
